import { useState, forwardRef, useMemo, useImperativeHandle, useRef, Fragment } from 'react';
import type { PropsWithoutRef, Ref, MouseEventHandler, ReactNode } from 'react';

import type {
  ForwardProps,
  HeadingTag,
  NoChildrenProp,
  OmitStrict,
  RefElement,
  TestIdProp,
  WithAttributes
} from '../../types';
import Button from '../Button';
import { focusHeadingOrContainer, withTestIds } from '../../utils';
import { useConsolidatedRef, useI18n, useTestIds, useTheme } from '../../hooks';
import Flex from '../Flex';
import Text from '../Text';
import Grid from '../Grid';
import ExpandCollapse, { ExpandCollapseButton } from '../ExpandCollapse';
import { Count } from '../Badges';
import Icon, { registerIcon } from '../Icon';
import * as timesIcon from '../Icon/icons/times.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import * as warnSolidIcon from '../Icon/icons/warn-solid.icon';
import * as diamondMinusIcon from '../Icon/icons/diamond-solid.icon';
import * as informationSolidIcon from '../Icon/icons/information-solid.icon';
import * as checkSolidIcon from '../Icon/icons/check-solid.icon';
import * as aiIcon from '../Icon/icons/polaris-solid.icon';
import VisuallyHiddenText from '../VisuallyHiddenText';
import HTML from '../HTML';

import { getBannerTestIds } from './Banner.test-ids';
import {
  StyledBanner,
  StyledBannerStatus,
  StyledBannerMessageList,
  StyledBannerMessage,
  StyledBannerMessageLabel
} from './Banner.styles';

registerIcon(
  timesIcon,
  caretRightIcon,
  warnSolidIcon,
  diamondMinusIcon,
  informationSolidIcon,
  checkSolidIcon,
  aiIcon
);

// FIXME: Temporarily re-exporting for backward compatibility.
export { StyledBanner, StyledBannerMessageList };

export interface BannerHandleValue {
  /** Used to apply focus to the banner via an imperative handle. */
  focus: () => void;
}

type Label =
  | string
  | {
      text: string;
      onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
      href?: string;
    };

export interface BannerMessage {
  label: Label | Label[];
  description?: string;
  action?: {
    text: string;
    onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    href?: string;
  };
  items?: (BannerMessage | string)[];
}

export type BannerProps = WithAttributes<
  'div',
  TestIdProp &
    NoChildrenProp & {
      /** Controls the styling of the Banner. */
      variant: 'urgent' | 'warning' | 'success' | 'info' | 'ai';
      /** Custom heading text. Default varies by variant. */
      headingText?: string;
      /**
       * The heading tag to render the provided heading as. This is agnostic to the styling of the heading.
       * @default 'h3'
       */
      headingTag?: HeadingTag;
      /**
       * Pass one or more descriptive messages to inform the user
       * @default []
       */
      messages?: (string | BannerMessage)[];
      /** Rich text content to render within banner. */
      content?: string;
      /** Callback when user explicitly dismisses the Banner. */
      onDismiss?: MouseEventHandler;
      /** Ref that can be used to access the imperative handle. */
      handle?: Ref<BannerHandleValue>;
    }
>;

const BannerMessageItem = forwardRef<
  HTMLLIElement,
  TestIdProp & OmitStrict<BannerMessage, 'items'> & ForwardProps
>(function BannerMessageItem({ testId, label, description, action, ...restProps }, ref) {
  const testIds = useTestIds(testId, getBannerTestIds);

  const labelArray = Array.isArray(label) ? label : [label];

  return (
    <StyledBannerMessage {...restProps} ref={ref}>
      <StyledBannerMessageLabel data-testid={testIds.messageLabel}>
        {labelArray.map((item, idx) =>
          typeof item === 'string' ? (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={idx}>{item}</Fragment>
          ) : (
            // eslint-disable-next-line react/no-array-index-key
            <Button variant='link' href={item.href} onClick={item.onClick} key={idx}>
              {item.text}
            </Button>
          )
        )}
      </StyledBannerMessageLabel>
      {description && (
        <>
          {' '}
          <Text data-testid={testIds.messageDescription} variant='secondary'>
            {description}
          </Text>
        </>
      )}
      {action && (
        <>
          {' '}
          <Text variant='secondary'>
            <Button
              data-testid={testIds.messageAction}
              variant='link'
              href={action.href}
              onClick={action.onClick}
              {...action}
            >
              {action.text}
            </Button>
          </Text>
        </>
      )}
    </StyledBannerMessage>
  );
});

const prepareMessages = (
  messages: (string | BannerMessage)[],
  testId: TestIdProp['testId']
): ReactNode[] => {
  return messages.flatMap(msg => {
    if (typeof msg === 'string') {
      return [<BannerMessageItem label={msg} />];
    }

    if (msg.items && msg.items.length > 0) {
      const { items, ...restMsg } = msg;
      const subMessages = prepareMessages(items, testId);
      return [<BannerMessageItem {...restMsg} testId={testId} />, subMessages];
    }

    return [<BannerMessageItem {...msg} testId={testId} />];
  });
};

export default withTestIds(
  forwardRef<RefElement<BannerProps>, PropsWithoutRef<BannerProps>>(function Banner(
    {
      testId,
      variant,
      headingText,
      headingTag = 'h3',
      messages = [],
      content,
      onDismiss,
      handle,
      ...restProps
    },
    ref
  ) {
    const testIds = useTestIds(testId, getBannerTestIds);
    const messagesToRender = useMemo(() => prepareMessages(messages, testId), [messages]);
    const firstMessageHasChildren =
      messages.length > 0 &&
      typeof messages[0] === 'object' &&
      Array.isArray(messages[0].items) &&
      messages[0].items.length > 0;
    const count = messages.length;
    const t = useI18n();
    const theme = useTheme();
    const [collapsed, setCollapsed] = useState(false);

    const bannerRef = useConsolidatedRef(ref);
    const expandCollapseBtnRef = useRef<HTMLButtonElement>(null);

    const showHeader =
      headingText !== undefined || (content ? count !== 0 : count !== 1 || firstMessageHasChildren);
    const expandable = showHeader && (count !== 0 || !!content);
    const showCount = count > 0;

    let heading = headingText;
    let icon: string;
    let role: string;
    let ariaLive: string;

    switch (variant) {
      case 'urgent':
        heading ??= t('error');
        icon = 'warn-solid';
        role = 'alert';
        ariaLive = 'assertive';
        break;
      case 'warning':
        heading ??= t('warning');
        icon = 'diamond-minus';
        role = 'alert';
        ariaLive = 'assertive';
        break;
      case 'success':
        heading ??= t('success');
        icon = theme.base['icon-set'] === 'budicon' ? 'check-solid' : 'check';
        role = 'none';
        ariaLive = 'polite';
        break;
      case 'ai':
        heading ??= t('ai_generated');
        icon = 'polaris-solid';
        role = 'none';
        ariaLive = 'polite';
        break;
      case 'info':
      default:
        heading ??= t('information');
        icon = 'information-solid';
        role = 'none';
        ariaLive = 'polite';
        break;
    }

    useImperativeHandle(
      handle,
      () => ({
        focus: () => {
          // Empty timeout ensures that the banner will take focus after any other programmatic focus events
          setTimeout(() => {
            if (expandCollapseBtnRef.current) {
              expandCollapseBtnRef.current.focus();
            } else if (bannerRef.current) {
              focusHeadingOrContainer(bannerRef.current, heading);
            }
          });
        }
      }),
      []
    );

    const iconRegion = (
      <Flex as={StyledBannerStatus} container={{ alignItems: 'center' }} variant={variant}>
        <Icon name={icon} />
      </Flex>
    );

    const dismissButton = onDismiss ? (
      <Button
        data-testid={testIds.dismiss}
        onClick={onDismiss}
        label={t('dismiss')}
        aria-label={t('banner_dismiss_button_label_a11y')}
        variant='simple'
        icon
      >
        <Icon name='times' />
      </Button>
    ) : null;

    return (
      <Grid
        data-testid={testIds.root}
        {...restProps}
        as={StyledBanner}
        container={
          showHeader
            ? { cols: 'minmax(0, 1fr)' }
            : { cols: 'min-content minmax(0, 1fr) auto', gap: 1, alignItems: 'start' }
        }
        role={role}
        aria-live={ariaLive}
        headingTag={headingTag}
        variant={variant}
        ref={bannerRef}
      >
        {!showHeader ? (
          <>
            {iconRegion}
            <VisuallyHiddenText
              as={headingTag}
            >{`${heading} ${showCount ? count : ''}`}</VisuallyHiddenText>
            <Flex item={{ alignSelf: 'center', grow: 1 }} as={content ? undefined : 'ul'}>
              {(() => {
                if (content) {
                  return <HTML content={content} />;
                }

                if (typeof messages[0] === 'string') {
                  return <BannerMessageItem testId={testId} label={messages[0]} as='li' />;
                }

                return <BannerMessageItem {...messages[0]} testId={testId} as='li' />;
              })()}
            </Flex>
            {dismissButton}
          </>
        ) : (
          <Flex container={{ direction: 'column', justify: 'center' }}>
            <Grid
              container={{
                cols: expandable ? 'auto 1fr auto' : '1fr auto',
                areas: expandable ? '"toggle heading dismiss"' : '"heading dismiss"',
                gap: 0.5,
                alignItems: 'start'
              }}
            >
              <Grid
                container={{ cols: 'min-content auto', gap: 1 }}
                item={{ area: 'heading', alignSelf: 'center' }}
              >
                {iconRegion}
                <Grid
                  data-testid={testIds.heading}
                  container={{
                    cols: `fit-content(100%) ${showCount ? 'max-content' : ''}`,
                    gap: 0.5,
                    alignItems: 'center'
                  }}
                  as={Text}
                  forwardedAs={headingTag}
                  variant='h3'
                >
                  {heading} {showCount && <Count>{count}</Count>}
                </Grid>
              </Grid>
              {expandable && (
                <Grid item={{ area: 'toggle' }}>
                  <ExpandCollapseButton
                    data-testid={testIds.expandCollapse}
                    onToggle={() => {
                      setCollapsed(cur => !cur);
                    }}
                    label={t(collapsed ? 'expand' : 'collapse')}
                    aria-label={t(collapsed ? 'expand_noun' : 'collapse_noun', [heading])}
                    aria-expanded={!collapsed}
                    iconOnly
                    ref={expandCollapseBtnRef}
                    expanded={!collapsed}
                  />
                </Grid>
              )}
              <Grid item={{ area: 'dismiss' }}>{dismissButton}</Grid>
            </Grid>
            {expandable && (
              <ExpandCollapse collapsed={collapsed}>
                <Flex
                  container={{
                    direction: 'column',
                    gap: 1,
                    pad: [1, 2]
                  }}
                >
                  {messagesToRender.length > 0 ? (
                    <StyledBannerMessageList items={messagesToRender} />
                  ) : null}
                  {content && <HTML content={content} />}
                </Flex>
              </ExpandCollapse>
            )}
          </Flex>
        )}
      </Grid>
    );
  }),
  getBannerTestIds
);
